import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Trophy, Check, ArrowRight, Tv, MessageSquare, BarChart3, Star, Lock } from 'lucide-react';
import { seasonAPI } from '../lib/api';
import { toast } from 'sonner';

const PLANS = [
  { key: 'free', label: 'Free', price: 0, capacity: 8, features: ['Standings', 'Announcements', 'Rules'] },
  { key: 'basic', label: 'Basic', price: 47, capacity: 8, features: ['Everything in Free', 'Match Centre', 'Chat', 'Team & Player Stats'] },
  { key: 'starter', label: 'Starter', price: 97, capacity: 25, features: ['Everything in Free', 'Match Centre', 'Chat', 'Team & Player Stats'] },
  { key: 'standard', label: 'Standard', price: 197, capacity: 99, features: ['Everything in Free', 'Match Centre', 'Chat', 'Team & Player Stats'] },
  { key: 'pro', label: 'Pro', price: 397, capacity: 400, features: ['Everything in Free', 'Match Centre', 'Chat', 'Team & Player Stats'] },
];

export default function PlansPage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState('starter');
  const [seasonLocked, setSeasonLocked] = useState(false);
  const [showLockedMsg, setShowLockedMsg] = useState(false);
  useEffect(() => { document.title = "Pool Plans & Pricing — Pools FC"; }, []);

  useEffect(() => {
    const check = async () => {
      try { const res = await seasonAPI.status(); setSeasonLocked(res.data.season_locked); } catch (e) { console.error(e); }
    };
    check();
  }, []);

  const handleContinue = () => {
    if (seasonLocked) {
      setShowLockedMsg(true);
      return;
    }
    localStorage.setItem('pfc_intent', 'start_pool');
    localStorage.setItem('pfc_selected_plan', selected);
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-white/95 backdrop-blur">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex h-[64px] sm:h-[120px] items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading font-bold text-lg">
            <img src="/poolsfc-logo.png" alt="Pools FC" className="h-[48px] w-[48px] sm:h-[104px] sm:w-[104px] rounded-lg" />
            Pools FC
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-muted-foreground hover:text-foreground">Already have an account? Sign In</Link>
          </div>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-10">
          <Badge className="bg-[#f3f0ff] text-[#5b3cc4] font-medium mb-3">START A POOL</Badge>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">Choose Your Pool Plan</h1>
          <p className="text-muted-foreground max-w-lg mx-auto">Select a plan based on your expected pool size. All paid plans include Match Centre, Chat, and full features. Free for entrants.</p>
        </div>

        <div className="max-w-md mx-auto mb-8">
          <label className="text-sm font-medium mb-2 block text-center">How many pool participants?</label>
          <input type="number" placeholder="e.g., 50" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-center"
            onChange={(e) => {
              const num = parseInt(e.target.value) || 0;
              if (num <= 8) setSelected('free');
              else if (num <= 25) setSelected('starter');
              else if (num <= 99) setSelected('standard');
              else setSelected('pro');
            }}
          />
          {selected && <p className="text-xs text-center text-[#5b3cc4] font-medium mt-2">Recommended: {PLANS.find(p => p.key === selected)?.label} plan (up to {PLANS.find(p => p.key === selected)?.capacity} entrants)</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {PLANS.map(plan => {
            const isSelected = selected === plan.key;
            const isPopular = plan.key === 'starter';
            return (
              <button key={plan.key} onClick={() => setSelected(plan.key)}
                className={`rounded-2xl border-2 p-5 text-left transition-all relative ${
                  isSelected ? 'border-[#5b3cc4] bg-[#f3f0ff]/30 ring-1 ring-[#5b3cc4]/30' : 'border-border hover:border-muted-foreground bg-white'
                }`}>
                {isPopular && <Badge className="bg-[#5b3cc4] text-white text-[9px] absolute -top-2.5 left-1/2 -translate-x-1/2">Popular</Badge>}
                <div className="font-heading text-2xl font-bold">{plan.price === 0 ? 'Free' : `$${plan.price}`}</div>
                {plan.price > 0 && <div className="text-xs text-muted-foreground">per season</div>}
                <div className="font-semibold text-sm mt-1">{plan.label}</div>
                <div className="text-xs text-muted-foreground mb-3">Up to {plan.capacity} entrants</div>
                <ul className="space-y-1">
                  {plan.features.map(f => (
                    <li key={f} className="text-xs flex items-start gap-1.5"><Check className="h-3 w-3 text-[#5b3cc4] flex-shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                {isSelected && <div className="mt-3 text-center"><Badge className="bg-[#5b3cc4] text-white">Selected</Badge></div>}
              </button>
            );
          })}
        </div>

        {/* Paid features preview */}
        {selected !== 'free' && (
          <div className="rounded-2xl border border-[#5b3cc4]/20 bg-[#f3f0ff]/20 p-6 mb-8">
            <h3 className="font-heading text-base font-bold mb-3 flex items-center gap-2"><Tv className="h-4 w-4 text-[#5b3cc4]" />Your paid plan includes:</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5 text-[#5b3cc4]" />Live EPL Fixtures</span>
              <span className="flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5 text-[#5b3cc4]" />League Table</span>
              <span className="flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-[#5b3cc4]" />Team & Player Stats</span>
              <span className="flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5 text-[#5b3cc4]" />Pool Chat</span>
            </div>
          </div>
        )}

        {showLockedMsg && (
          <div className="rounded-xl bg-yellow-50 border border-yellow-200 px-5 py-4 flex items-start gap-3 mb-4">
            <Lock className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-yellow-800">Season is Currently Locked</p>
              <p className="text-sm text-yellow-700 mt-1">New pools cannot be created at this time. The current season is in progress and all pools are locked. Please check back when the next season opens.</p>
            </div>
          </div>
        )}

        <Button size="lg" className="w-full bg-[#5b3cc4] hover:bg-[#4e33a8] text-base" onClick={handleContinue}>
          Continue with {PLANS.find(p => p.key === selected)?.label} Plan <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
        <p className="text-center text-xs text-muted-foreground mt-3">You'll create your account next, then set up your pool.</p>

        {/* Feature Illustrations */}
        <div className="mt-12">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl font-bold">What's Included in Paid Plans</h2>
            <p className="text-muted-foreground text-sm mt-2">All paid plans include full access to these features</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Match Centre */}
            <div className="rounded-2xl border bg-[#fafafa] overflow-hidden shadow-sm">
              <div className="flex items-center gap-2 px-5 py-3 bg-white border-b">
                <div className="flex gap-1.5"><div className="h-2.5 w-2.5 rounded-full bg-red-400" /><div className="h-2.5 w-2.5 rounded-full bg-yellow-400" /><div className="h-2.5 w-2.5 rounded-full bg-green-400" /></div>
                <span className="text-sm font-semibold text-gray-600 ml-2">Match Centre</span>
                <Badge className="ml-auto bg-green-100 text-green-700 text-[9px]">LIVE</Badge>
              </div>
              <div className="p-5 space-y-2">
                {[
                  { home: 'Arsenal', hScore: 2, away: 'Chelsea', aScore: 1, status: 'FT' },
                  { home: 'Liverpool', hScore: 3, away: 'Man City', aScore: 0, status: 'FT' },
                  { home: 'Brentford', hScore: null, away: 'Brighton', aScore: null, status: 'Sat 15:00' },
                ].map((m, i) => (
                  <div key={m.home} className="flex items-center justify-between rounded-lg bg-white p-2.5 border text-sm">
                    <span className="font-medium w-24 text-right">{m.home}</span>
                    <span className="font-heading font-bold text-center w-16">{m.hScore !== null ? `${m.hScore} - ${m.aScore}` : m.status}</span>
                    <span className="font-medium w-24">{m.away}</span>
                  </div>
                ))}
              </div>
              <div className="px-5 pb-4"><p className="text-xs text-muted-foreground">Live fixtures, league table, and full season schedule — all inside your pool page.</p></div>
            </div>

            {/* Team Statistics */}
            <div className="rounded-2xl border bg-[#fafafa] overflow-hidden shadow-sm">
              <div className="flex items-center gap-2 px-5 py-3 bg-white border-b">
                <div className="flex gap-1.5"><div className="h-2.5 w-2.5 rounded-full bg-red-400" /><div className="h-2.5 w-2.5 rounded-full bg-yellow-400" /><div className="h-2.5 w-2.5 rounded-full bg-green-400" /></div>
                <span className="text-sm font-semibold text-gray-600 ml-2">Team & Player Stats</span>
              </div>
              <div className="p-5">
                <div className="rounded-xl overflow-hidden border">
                  <div className="bg-gradient-to-r from-[#5b3cc4] to-[#6366f1] px-4 py-3 text-white flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center font-heading font-bold text-sm">ARS</div>
                    <div className="flex-1">
                      <div className="text-base font-bold">Arsenal</div>
                      <div className="text-white/70 text-[10px]">Tier 1 · Win 2x</div>
                    </div>
                    <div className="text-right"><div className="text-2xl font-heading font-bold">47</div><div className="text-white/60 text-[9px]">Pool Pts</div></div>
                  </div>
                  <div className="grid grid-cols-4 divide-x text-center py-2">
                    {[{l:'Played',v:'30'},{l:'Won',v:'20'},{l:'Drawn',v:'7'},{l:'Lost',v:'3'}].map(s => (
                      <div key={s.l}><div className="font-bold text-sm">{s.v}</div><div className="text-[9px] text-muted-foreground">{s.l}</div></div>
                    ))}
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {[
                    { rank: 1, name: 'E. Haaland', team: 'Man City', goals: 22 },
                    { rank: 2, name: 'Thiago', team: 'Brentford', goals: 18 },
                  ].map(p => (
                    <div key={p.rank} className="flex items-center gap-2 rounded-lg bg-white p-2 border">
                      <span className="text-xs font-bold text-muted-foreground w-5">{p.rank}</span>
                      <div className="h-6 w-6 rounded-full bg-[#5b3cc4]/10 flex items-center justify-center"><Star className="h-3 w-3 text-[#5b3cc4]" /></div>
                      <div className="flex-1"><div className="text-xs font-medium">{p.name}</div><div className="text-[9px] text-muted-foreground">{p.team}</div></div>
                      <span className="font-heading font-bold text-sm text-[#5b3cc4]">{p.goals}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-5 pb-4"><p className="text-xs text-muted-foreground">Full team stats, streaks, goals by time, and top 20 scorers & assists with player profiles.</p></div>
            </div>

            {/* Pool Chat */}
            <div className="rounded-2xl border bg-[#fafafa] overflow-hidden shadow-sm">
              <div className="flex items-center gap-2 px-5 py-3 bg-white border-b">
                <div className="flex gap-1.5"><div className="h-2.5 w-2.5 rounded-full bg-red-400" /><div className="h-2.5 w-2.5 rounded-full bg-yellow-400" /><div className="h-2.5 w-2.5 rounded-full bg-green-400" /></div>
                <span className="text-sm font-semibold text-gray-600 ml-2">Pool Chat</span>
              </div>
              <div className="p-5 space-y-2">
                {[
                  { name: 'John C.', msg: 'Arsenal just scored! +2 for Tier 1 picks', role: 'Organizer' },
                  { name: 'Sarah M.', msg: 'My Sunderland pick is incredible this season!', role: null },
                  { name: 'Kyle R.', msg: 'Who has the closest goals prediction?', role: null },
                ].map((m, i) => (
                  <div key={m.name} className="flex gap-2 rounded-lg bg-white p-2.5 border">
                    <div className="h-7 w-7 rounded-full bg-[#5b3cc4] flex-shrink-0 flex items-center justify-center"><span className="text-[9px] font-bold text-white">{m.name[0]}</span></div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold">{m.name}</span>
                        {m.role && <span className="text-[7px] bg-[#f3f0ff] text-[#5b3cc4] px-1.5 py-0.5 rounded font-medium">{m.role}</span>}
                      </div>
                      <p className="text-xs text-muted-foreground">{m.msg}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-5 pb-4"><p className="text-xs text-muted-foreground">Real-time discussions. Celebrate wins, debate picks, talk strategy all season.</p></div>
            </div>

            {/* Weekly Breakdown */}
            <div className="rounded-2xl border bg-[#fafafa] overflow-hidden shadow-sm">
              <div className="flex items-center gap-2 px-5 py-3 bg-white border-b">
                <div className="flex gap-1.5"><div className="h-2.5 w-2.5 rounded-full bg-red-400" /><div className="h-2.5 w-2.5 rounded-full bg-yellow-400" /><div className="h-2.5 w-2.5 rounded-full bg-green-400" /></div>
                <span className="text-sm font-semibold text-gray-600 ml-2">Weekly Breakdown</span>
              </div>
              <div className="p-5">
                <table className="w-full text-xs">
                  <thead><tr className="border-b">
                    <th className="py-1.5 text-left text-muted-foreground">Week</th>
                    <th className="py-1.5 text-center">Arsenal</th><th className="py-1.5 text-center">Villa</th>
                    <th className="py-1.5 text-center">Palace</th><th className="py-1.5 text-right font-bold text-[#5b3cc4]">Total</th>
                  </tr></thead>
                  <tbody>
                    {[
                      { gw: 'GW1', t1: 'W+2', t2: 'W+4', t3: 'D+3', total: 19 },
                      { gw: 'GW2', t1: 'D+1', t2: 'L+0', t3: 'W+6', total: 39 },
                      { gw: 'GW3', t1: 'W+2', t2: 'W+4', t3: 'L+0', total: 53 },
                    ].map(r => (
                      <tr key={r.gw} className="border-b">
                        <td className="py-1.5 text-muted-foreground">{r.gw}</td>
                        <td className="py-1.5 text-center"><span className={r.t1[0]==='W'?'text-green-600 font-semibold':r.t1[0]==='D'?'text-yellow-600':'text-red-500'}>{r.t1}</span></td>
                        <td className="py-1.5 text-center"><span className={r.t2[0]==='W'?'text-green-600 font-semibold':r.t2[0]==='D'?'text-yellow-600':'text-red-500'}>{r.t2}</span></td>
                        <td className="py-1.5 text-center"><span className={r.t3[0]==='W'?'text-green-600 font-semibold':r.t3[0]==='D'?'text-yellow-600':'text-red-500'}>{r.t3}</span></td>
                        <td className="py-1.5 text-right font-bold text-[#5b3cc4]">{r.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 pb-4"><p className="text-xs text-muted-foreground">Click any entry to see week-by-week point accumulation across all 5 tier picks.</p></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
